import "@/App.css";
import { LocaleProvider } from "@/lib/i18n";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";

import FlowPrototype from "@/pages/preview/FlowPrototype";
import Home from "@/pages/marketing/Home";
import Destinations from "@/pages/marketing/Destinations";
import DestinationDetail from "@/pages/marketing/DestinationDetail";
import HowItWorks from "@/pages/marketing/HowItWorks";
import ForApplicants from "@/pages/marketing/ForApplicants";
import ForAgencies from "@/pages/marketing/ForAgencies";
import Consultation from "@/pages/marketing/Consultation";
import LegalPage from "@/pages/marketing/LegalPage";
import { Login, Register } from "@/pages/auth/AuthPages";
import AuthCallback from "@/pages/auth/AuthCallback";

import Dashboard from "@/pages/app/Dashboard";
import NewCase from "@/pages/app/NewCase";
import CaseView from "@/pages/app/CaseView";
import CaseChat from "@/pages/app/CaseChat";
import DocumentDetail from "@/pages/app/DocumentDetail";
import Notifications from "@/pages/app/Notifications";

import { AgencyDashboard, AgencyPipeline, AgencyClients, AgencyReviews } from "@/pages/agency/AgencyPages";
import { AgencyCase } from "@/pages/agency/AgencyCase";
import { AdminOverview, AdminUsers, AdminOrgs } from "@/pages/admin/AdminPages";
import { KnowledgeHome, KnowledgeSet } from "@/pages/admin/AdminKnowledge";
import AdminDraft from "@/pages/admin/AdminDraft";
import { AdminCases, AdminAutomations, AdminAudit } from "@/pages/admin/AdminOps";
import AdminPerformance from "@/pages/admin/AdminPerformance";
import { ReviewerQueue, ReviewerCase } from "@/pages/reviewer/ReviewerPages";

const APPLICANT = ["applicant"];
const AGENCY = ["agency_owner", "agency_manager", "agency_agent", "administrator", "super_administrator"];
const ADMIN = ["administrator", "super_administrator"];
const REVIEWER = ["reviewer", "administrator", "super_administrator"];

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppRoutes() {
  const location = useLocation();
  // Emergent OAuth returns to /app#session_id=... intercept before ProtectedRoute
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<Home />} />
      <Route path="/destinations" element={<Destinations />} />
      <Route path="/destinations/:key" element={<DestinationDetail />} />
      <Route path="/how-it-works" element={<HowItWorks />} />
      <Route path="/applicants" element={<ForApplicants />} />
      <Route path="/agencies" element={<ForAgencies />} />
      <Route path="/consultation" element={<Consultation />} />
      <Route path="/book" element={<Consultation />} />
      <Route path="/privacy" element={<LegalPage page="privacy" />} />
      <Route path="/terms" element={<LegalPage page="terms" />} />
      <Route path="/help" element={<LegalPage page="help" />} />
      <Route path="/preview/flow" element={<FlowPrototype />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />

      {/* Applicant */}
      <Route path="/app" element={<ProtectedRoute roles={APPLICANT}><Dashboard /></ProtectedRoute>} />
      <Route path="/app/new" element={<ProtectedRoute roles={APPLICANT}><NewCase /></ProtectedRoute>} />
      <Route path="/app/notifications" element={<ProtectedRoute roles={APPLICANT}><Notifications /></ProtectedRoute>} />
      <Route path="/app/cases/:id" element={<ProtectedRoute roles={APPLICANT}><CaseChat /></ProtectedRoute>} />
      <Route path="/app/cases/:id/file" element={<ProtectedRoute roles={APPLICANT}><CaseView /></ProtectedRoute>} />
      <Route path="/app/cases/:caseId/documents/:docId" element={<ProtectedRoute roles={APPLICANT}><DocumentDetail /></ProtectedRoute>} />

      {/* Agency */}
      <Route path="/agency" element={<ProtectedRoute roles={AGENCY}><AgencyDashboard /></ProtectedRoute>} />
      <Route path="/agency/pipeline" element={<ProtectedRoute roles={AGENCY}><AgencyPipeline /></ProtectedRoute>} />
      <Route path="/agency/clients" element={<ProtectedRoute roles={AGENCY}><AgencyClients /></ProtectedRoute>} />
      <Route path="/agency/reviews" element={<ProtectedRoute roles={AGENCY}><AgencyReviews /></ProtectedRoute>} />
      <Route path="/agency/cases/:id" element={<ProtectedRoute roles={AGENCY}><AgencyCase /></ProtectedRoute>} />

      {/* Reviewer */}
      <Route path="/reviewer" element={<ProtectedRoute roles={REVIEWER}><ReviewerQueue /></ProtectedRoute>} />
      <Route path="/reviewer/cases/:id" element={<ProtectedRoute roles={REVIEWER}><ReviewerCase /></ProtectedRoute>} />

      {/* Admin */}
      <Route path="/admin" element={<ProtectedRoute roles={ADMIN}><AdminOverview /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute roles={ADMIN}><AdminUsers /></ProtectedRoute>} />
      <Route path="/admin/organizations" element={<ProtectedRoute roles={ADMIN}><AdminOrgs /></ProtectedRoute>} />

      <Route path="/admin/knowledge" element={<ProtectedRoute roles={ADMIN}><KnowledgeHome /></ProtectedRoute>} />
      <Route path="/admin/knowledge/sets/:id" element={<ProtectedRoute roles={ADMIN}><KnowledgeSet /></ProtectedRoute>} />
      <Route path="/admin/knowledge/drafts/:id" element={<ProtectedRoute roles={ADMIN}><AdminDraft /></ProtectedRoute>} />
      <Route path="/admin/cases" element={<ProtectedRoute roles={ADMIN}><AdminCases /></ProtectedRoute>} />
      <Route path="/admin/automations" element={<ProtectedRoute roles={ADMIN}><AdminAutomations /></ProtectedRoute>} />
      <Route path="/admin/performance" element={<ProtectedRoute roles={ADMIN}><AdminPerformance /></ProtectedRoute>} />
      <Route path="/admin/audit" element={<ProtectedRoute roles={ADMIN}><AdminAudit /></ProtectedRoute>} />

      <Route path="*" element={<Home />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <LocaleProvider>
        <BrowserRouter>
          <ScrollToTop />
          <AppRoutes />
          <Toaster position="top-center" richColors />
        </BrowserRouter>
        </LocaleProvider>
      </AuthProvider>
    </div>
  );
}
